/**
 * MigratingTab — Self-contained tab for migrating legacy 2.0 data to 4.0.
 *
 * Uses a pp-subtab-toggle segmented control to switch between migration
 * categories. Each category shows a single pp-section card with a checkbox
 * table and migrate action.
 *
 * Categories: Groups, Users, AFR Devices, HP Alerts, Reason Codes, Alert Settings
 *
 * Props:
 *   campusId     — current campus ID (number)
 *   campusGuid   — campus GUID for DynamoDB partition key
 *   editRights   — boolean (can migrate)
 *   showToast    — (type, message) => void
 *   getSessionParams — () => { campusId, userId, clientId }
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { getAccessSettings } from '../../../../../libs.jsx';
import {
  fetchDDBUsers, fetchDDBGroups, fetchDDBAFR, fetchDDBHP,
  fetchDDBAlertSettings, fetchDDBReasonCodes,
  fetchMyUsers, fetchMyGroups, fetchMyAFR, fetchMyHP,
  fetchMyReasonCodes, fetchMyAlertSettings,
  fetchEventTypes, fetchDeviceTypes,
  migrateUsers, migrateGroups, migrateAFRDevices,
  migrateHPAlerts, migrateReasonCodes,
  migrateAlertSettings, migrateCustomSettings,
} from '../api/campusProfileApi.js';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Sort comparator */
const sortBy = (arr, key, order = 'asc') => {
  return [...arr].sort((a, b) => {
    const aVal = (a[key] || '').toString().toLowerCase();
    const bVal = (b[key] || '').toString().toLowerCase();
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
};

/** Checkbox table for selectable items */
const CheckboxTable = ({ columns, data, idKey, selected, onToggle, onToggleAll }) => {
  const allSelected = data.length > 0 && selected.length === data.length;

  return (
    <div className="dp-table-wrap" style={{ minWidth: '400px' }}>
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            <th className="dp-th" style={{ width: '40px' }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => onToggleAll()}
              />
            </th>
            {columns.map((col) => (
              <th key={col.key} className="dp-th">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => {
            const id = row[idKey] ?? i;
            const isChecked = selected.includes(id);
            return (
              <tr key={id} className="dp-tr">
                <td className="dp-td" style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggle(id)}
                  />
                </td>
                {columns.map((col) => (
                  <td key={col.key} className="dp-td">{row[col.key] ?? '\u2014'}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────────────

const MigratingTab = ({ campusId, campusGuid, editRights, showToast, getSessionParams }) => {
  // ── Data State ────────────────────────────────────────────────────────────
  // DDB (2.0)
  const [ddbUsers, setDdbUsers] = useState([]);
  const [ddbGroups, setDdbGroups] = useState([]);
  const [ddbAFR, setDdbAFR] = useState([]);
  const [ddbHP, setDdbHP] = useState([]);
  const [ddbAlerts, setDdbAlerts] = useState([]);
  const [ddbReasonCodes, setDdbReasonCodes] = useState([]);

  // 4.0 (SQL)
  const [myUsers, setMyUsers] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [myAFR, setMyAFR] = useState([]);
  const [myHP, setMyHP] = useState([]);
  const [myReasonCodes, setMyReasonCodes] = useState([]);
  const [myAlerts, setMyAlerts] = useState([]);

  // Lookup tables
  const [eventTypes, setEventTypes] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);

  // Loading / migrating
  const [loaded, setLoaded] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Sub-tab + selection state
  const [subTab, setSubTab] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedAFR, setSelectedAFR] = useState([]);
  const [selectedHP, setSelectedHP] = useState([]);
  const [selectedReasons, setSelectedReasons] = useState([]);

  // ── Data Loading ──────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!campusGuid) {
      setLoaded(true);
      return;
    }

    const results = await Promise.all([
      // DDB reads
      fetchDDBUsers(campusGuid, campusId),
      fetchDDBGroups(campusGuid, campusId),
      fetchDDBAFR(campusGuid, campusId),
      fetchDDBHP(campusGuid, campusId),
      fetchDDBAlertSettings(campusGuid, campusId),
      fetchDDBReasonCodes(campusGuid, campusId),
      // 4.0 reads
      fetchMyUsers(campusId),
      fetchMyGroups(campusId),
      fetchMyAFR(campusId),
      fetchMyHP(campusId),
      fetchMyReasonCodes(campusId),
      fetchMyAlertSettings(campusId),
      // Lookup tables
      fetchEventTypes(campusId),
      fetchDeviceTypes(campusId),
    ]);

    setDdbUsers(results[0] || []);
    setDdbGroups(results[1] || []);
    setDdbAFR(results[2] || []);
    setDdbHP(results[3] || []);
    setDdbAlerts(results[4] || []);
    setDdbReasonCodes(results[5] || []);
    setMyUsers(results[6] || []);
    setMyGroups(results[7] || []);
    setMyAFR(results[8] || []);
    setMyHP(results[9] || []);
    setMyReasonCodes(results[10] || []);
    setMyAlerts(results[11] || []);
    setEventTypes(results[12] || []);
    setDeviceTypes(results[13] || []);
    setLoaded(true);
  }, [campusId, campusGuid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Computed datasets (2.0 items not yet in 4.0) ──────────────────────────

  /** Users available to migrate = DDB users not in 4.0 (by cognito_id/UserId) */
  const activeUsers = useMemo(() => {
    const existing = new Set(myUsers.map((u) => u.cognito_id));
    return ddbUsers.filter((u) => u.UserId && !existing.has(u.UserId));
  }, [ddbUsers, myUsers]);

  /** Groups available to migrate = DDB groups not in 4.0 (by group_guid/GroupId) */
  const activeGroups = useMemo(() => {
    const existing = new Set(myGroups.map((g) => g.group_guid));
    return ddbGroups.filter((g) => g.GroupId && !existing.has(g.GroupId));
  }, [ddbGroups, myGroups]);

  /** AFR devices available to migrate */
  const [afrDataSet, afrOriginIds] = useMemo(() => {
    const myIds = myAFR.map((a) => a.device_type_id);
    const dataSet = [];
    const afrDevices = ddbAFR.length > 0 && ddbAFR[0]?.AFRDevices ? ddbAFR[0].AFRDevices : [];

    for (const devName of afrDevices) {
      for (const dt of deviceTypes) {
        if (devName.toLowerCase() === dt.device_type_name?.toLowerCase() && !myIds.includes(dt.inovonics_type_id)) {
          dataSet.push({ device_type_id: dt.inovonics_type_id, device_type_name: dt.device_type_name });
          break;
        }
      }
    }
    return [dataSet, myIds];
  }, [ddbAFR, myAFR, deviceTypes]);

  /** HP alert types available to migrate */
  const [hpDataSet, hpOriginIds] = useMemo(() => {
    const myIds = myHP.map((h) => h.event_type_id);
    const dataSet = [];
    const hpTypes = ddbHP.length > 0 && ddbHP[0]?.HighPriorityAlertTypes ? ddbHP[0].HighPriorityAlertTypes : [];

    for (const hpName of hpTypes) {
      for (const et of eventTypes) {
        const match =
          (hpName.toLowerCase() === 'alert' && et.event_type_name?.toLowerCase() === 'emergency') ||
          hpName.toLowerCase() === et.event_type_name?.toLowerCase();
        if (match && !myIds.includes(et.event_type_id)) {
          dataSet.push({ event_type_id: et.event_type_id, event_type_name: et.event_type_name });
          break;
        }
      }
    }
    return [dataSet, myIds];
  }, [ddbHP, myHP, eventTypes]);

  /** Reason codes available to migrate */
  const [reasonDataSet, reasonOriginIds] = useMemo(() => {
    const myIds = myReasonCodes.map((r) => r.reason_code_id);
    const dataSet = [];

    for (const rc of ddbReasonCodes) {
      const rcId = parseInt(rc.ReasonCodeId, 10);
      if (!myIds.includes(rcId)) {
        dataSet.push({
          reason_code_description: rc.Description,
          reason_code_id: rcId,
          pal_reason_code_id: parseInt(rc.GlobalReasonCode, 10),
        });
      }
    }
    return [dataSet, myIds];
  }, [ddbReasonCodes, myReasonCodes]);

  /** Alert settings available to migrate (2.0 has settings AND 4.0 does NOT) */
  const alertSettingsAvailable = useMemo(() => {
    return ddbAlerts.length > 0 && (!myAlerts || myAlerts.length === 0);
  }, [ddbAlerts, myAlerts]);

  const hasAnything = activeUsers.length > 0 || activeGroups.length > 0 || afrDataSet.length > 0 ||
    hpDataSet.length > 0 || reasonDataSet.length > 0 || alertSettingsAvailable;

  // ── Available sub-tab categories ────────────────────────────────────────
  const categories = useMemo(() => {
    const list = [];
    if (activeGroups.length > 0)   list.push({ key: 'groups',  label: 'Groups',    count: activeGroups.length });
    if (activeUsers.length > 0)    list.push({ key: 'users',   label: 'Users',     count: activeUsers.length });
    if (afrDataSet.length > 0)     list.push({ key: 'afr',     label: 'AFR',       count: afrDataSet.length });
    if (hpDataSet.length > 0)      list.push({ key: 'hp',      label: 'HP Alerts', count: hpDataSet.length });
    if (reasonDataSet.length > 0)  list.push({ key: 'reasons', label: 'Reasons',   count: reasonDataSet.length });
    if (alertSettingsAvailable)    list.push({ key: 'alerts',  label: 'Settings',  count: 1 });
    return list;
  }, [activeGroups, activeUsers, afrDataSet, hpDataSet, reasonDataSet, alertSettingsAvailable]);

  // Auto-select first available category when data loads
  useEffect(() => {
    if (loaded && categories.length > 0 && (!subTab || !categories.find((c) => c.key === subTab))) {
      setSubTab(categories[0].key);
    }
  }, [loaded, categories, subTab]);

  // Reset confirming state when switching sub-tabs
  const handleSubTabChange = useCallback((key) => {
    setSubTab(key);
    setConfirming(false);
  }, []);

  // ── Toggle helpers ────────────────────────────────────────────────────────
  const toggle = (setter, id) => {
    setter((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = (setter, data, idKey) => {
    setter((prev) => prev.length === data.length ? [] : data.map((d) => d[idKey]));
  };

  // ── Migration handlers ────────────────────────────────────────────────────

  /** Migrate selected users */
  const handleMigrateUsers = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();
    const payload = [];

    for (const user of activeUsers) {
      if (selectedUsers.includes(user.UserId)) {
        let role;
        switch (user.UserRole) {
          case 'admin': role = 5; break;
          case 'corporate': role = 4; break;
          case 'corporate-admin': case 'palcare': role = 3; break;
          case 'palcare-admin': role = 1; break;
          default: role = 6;
        }

        const pAccess = getAccessSettings(parseInt(role, 10));

        // Map group GUIDs to 4.0 group IDs
        const groupIds = [];
        for (const g of myGroups) {
          if (user.GroupIds && user.GroupIds.includes(g.group_guid) && !groupIds.includes(g.group_id)) {
            groupIds.push(g.group_id);
          }
        }

        payload.push({
          Action: 'migrate',
          DisplayName: user.DisplayName,
          UserEmail: user.UserEmail || user.Email || '',
          UserRole: parseInt(role, 10),
          UserId: user.UserId,
          Migrated: true,
          CampusGuid: campusGuid,
          PortalAccess: pAccess,
          GroupIds: groupIds,
          Username: user.UserName,
          CampusId: campusId,
          ClientId: session.clientId,
        });
      }
    }

    const result = await migrateUsers(payload, session.userId);
    setMigrating(false);
    setConfirming(false);

    if (result && result[0]) {
      showToast('success', 'All selected users migrated.');
      setSelectedUsers([]);
      loadData();
    } else {
      showToast('error', 'Error during user migration. Check user table.');
    }
  }, [activeUsers, selectedUsers, myGroups, campusId, campusGuid, getSessionParams, showToast, loadData]);

  /** Migrate selected groups */
  const handleMigrateGroups = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();
    const payload = [];

    for (const group of activeGroups) {
      if (selectedGroups.includes(group.GroupId)) {
        payload.push({
          campus_id: campusId,
          group_name: group.GroupName,
          capcode: group.CapCode,
          group_guid: group.GroupId,
        });
      }
    }

    const result = await migrateGroups(payload, session);
    setMigrating(false);
    setConfirming(false);

    if (result && result[0]) {
      showToast('success', 'Groups migrated successfully.');
      setSelectedGroups([]);
      loadData();
    } else {
      showToast('error', 'Error during group migration.');
    }
  }, [activeGroups, selectedGroups, campusId, getSessionParams, showToast, loadData]);

  /** Migrate selected AFR devices */
  const handleMigrateAFR = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();

    const newAfrList = [...selectedAFR, ...afrOriginIds].map((id) => `${id}-1`);
    const payload = [{ campus_id: campusId, afr_entities: newAfrList }];
    const oldPayload = [{ campusId, afr_entities: afrOriginIds }];

    const result = await migrateAFRDevices(payload, oldPayload, session);
    setMigrating(false);
    setConfirming(false);

    if (result && result[0]) {
      showToast('success', 'AFR devices migrated successfully.');
      setSelectedAFR([]);
      loadData();
    } else {
      showToast('error', 'Error during AFR migration.');
    }
  }, [selectedAFR, afrOriginIds, campusId, getSessionParams, showToast, loadData]);

  /** Migrate selected HP alert types */
  const handleMigrateHP = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();

    const payload = [{ campus_id: campusId, priority_entities: [...selectedHP, ...hpOriginIds] }];
    const oldPayload = [{ campusId, priority_entities: hpOriginIds }];

    const result = await migrateHPAlerts(payload, oldPayload, session);
    setMigrating(false);
    setConfirming(false);

    if (result && result[0]) {
      showToast('success', 'High priority alert types migrated successfully.');
      setSelectedHP([]);
      loadData();
    } else {
      showToast('error', 'Error during HP alert migration.');
    }
  }, [selectedHP, hpOriginIds, campusId, getSessionParams, showToast, loadData]);

  /** Migrate selected reason codes */
  const handleMigrateReasons = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();

    const payload = reasonDataSet
      .filter((r) => selectedReasons.includes(r.reason_code_id))
      .map((r) => ({
        reason_code_description: r.reason_code_description,
        pal_reason_code_id: r.pal_reason_code_id,
        reason_code_id: r.reason_code_id,
        campus_id: campusId,
      }));

    const result = await migrateReasonCodes(payload, session);
    setMigrating(false);
    setConfirming(false);

    if (result && result[0]) {
      showToast('success', 'Reason codes migrated successfully.');
      setSelectedReasons([]);
      loadData();
    } else {
      showToast('error', 'Error during reason code migration.');
    }
  }, [reasonDataSet, selectedReasons, campusId, getSessionParams, showToast, loadData]);

  /** Migrate alert & custom settings */
  const handleMigrateAlerts = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();
    const a = ddbAlerts[0];

    const acceptOn = a.AcceptButtonEnabled ? 1 : 0;
    const finishOn = a.FinishButtonEnabled ? 1 : 0;
    const reasonOn = a.ReasonCodeEnabled ? 1 : 0;
    const releaseTimer = a.ReleaseTimeInterval || 60;
    const finishTimer = a.FinishTimeInterval || 60;
    const clearTimer = a.ClearTimeInterval || 59;

    const alertPayload = [{
      campus_id: campusId,
      accept_required: acceptOn,
      finish_required: finishOn,
      reason_required: reasonOn,
      release_timeout: parseInt(releaseTimer, 10) * 60,
      finish_timeout: parseInt(finishTimer, 10) * 60,
      clear_timeout: parseInt(clearTimer, 10) * 60,
    }];

    const customPayload = [{
      campus_id: campusId,
      is_name_editable: 0,
      is_group_editable: 0,
      modified_by: session.userId,
    }];

    const [alertResult, customResult] = await Promise.all([
      migrateAlertSettings(alertPayload, session),
      migrateCustomSettings(customPayload, session),
    ]);

    setMigrating(false);
    setConfirming(false);

    if (alertResult?.[0] && customResult?.[0]) {
      showToast('success', 'Alert & customization settings migrated successfully.');
      loadData();
    } else {
      showToast('error', 'Error during alert/custom settings migration.');
    }
  }, [ddbAlerts, campusId, getSessionParams, showToast, loadData]);

  // ── Per-category config lookup ─────────────────────────────────────────

  /** Get the active selection count, handler, and confirm text for the current sub-tab */
  const getSubTabConfig = () => {
    switch (subTab) {
      case 'groups':  return { selected: selectedGroups.length,  onMigrate: handleMigrateGroups,  confirm: 'Groups will be migrated to 4.0. This cannot be undone.' };
      case 'users':   return { selected: selectedUsers.length,   onMigrate: handleMigrateUsers,   confirm: 'Users will be migrated to 4.0. This cannot be undone.' };
      case 'afr':     return { selected: selectedAFR.length,     onMigrate: handleMigrateAFR,     confirm: 'AFR device types will be migrated to 4.0. This cannot be undone.' };
      case 'hp':      return { selected: selectedHP.length,      onMigrate: handleMigrateHP,      confirm: 'High priority event types will be migrated to 4.0. This cannot be undone.' };
      case 'reasons': return { selected: selectedReasons.length, onMigrate: handleMigrateReasons, confirm: 'Reason codes will be migrated to 4.0. This cannot be undone.' };
      case 'alerts':  return { selected: 1,                      onMigrate: handleMigrateAlerts,  confirm: 'This will overwrite any current alert/custom settings.' };
      default:        return { selected: 0, onMigrate: null, confirm: '' };
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!loaded) {
    return <p style={{ padding: '1rem', color: 'var(--color-text-tertiary, #9ca3af)' }}>Loading migration data...</p>;
  }

  if (!campusGuid) {
    return (
      <div className="cpp-int-empty">
        <p className="cpp-int-empty__title">No campus GUID available</p>
        <p className="cpp-int-empty__subtitle">Migration requires a legacy 2.0 campus GUID.</p>
      </div>
    );
  }

  if (!hasAnything) {
    return (
      <div className="cpp-int-empty">
        <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="var(--color-text-tertiary, #9ca3af)" className="cpp-int-empty__icon" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        <p className="cpp-int-empty__title">No assets to migrate</p>
        <p className="cpp-int-empty__subtitle">All legacy 2.0 data has already been migrated to 4.0.</p>
      </div>
    );
  }

  const sortedUsers = sortBy(activeUsers, 'UserName');
  const sortedGroups = sortBy(activeGroups, 'GroupName');
  const sortedAFR = sortBy(afrDataSet, 'device_type_name');
  const sortedHP = sortBy(hpDataSet, 'event_type_name');
  const sortedReasons = sortBy(reasonDataSet, 'reason_code_description');

  const cfg = getSubTabConfig();
  const activeCat = categories.find((c) => c.key === subTab);
  const sectionTitle = activeCat
    ? `2.0 ${activeCat.label}${activeCat.count != null ? ` (${activeCat.count})` : ''}`
    : '';

  return (
    <>
      {/* Segmented control — category picker */}
      <div className="pp-subtab-toggle" style={{ flexWrap: 'wrap' }}>
        {categories.map((cat) => (
          <button
            key={cat.key}
            className={`pp-subtab-btn${subTab === cat.key ? ' is-active' : ''}`}
            onClick={() => handleSubTabChange(cat.key)}
          >
            {cat.label}
            {cat.count != null && (
              <span className="cpp-mig-badge">{cat.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Active category content — single pp-section card */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">{sectionTitle}</h3>
          <div className="pp-section__actions">
            {editRights && cfg.onMigrate && (
              <>
                {confirming ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-danger, #c53030)' }}>
                      {cfg.confirm}
                    </span>
                    <button
                      type="button"
                      className="dp-btn dp-btn--cancel dp-btn--sm"
                      onClick={() => setConfirming(false)}
                      disabled={migrating}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="dp-btn dp-btn--primary dp-btn--sm"
                      onClick={() => cfg.onMigrate()}
                      disabled={migrating}
                    >
                      {migrating ? 'Migrating...' : 'Confirm'}
                    </button>
                  </div>
                ) : (
                  cfg.selected > 0 && (
                    <button
                      type="button"
                      className="dp-btn dp-btn--primary dp-btn--sm"
                      onClick={() => setConfirming(true)}
                      disabled={migrating}
                    >
                      <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                      </svg>
                      Migrate ({cfg.selected})
                    </button>
                  )
                )}
              </>
            )}
          </div>
        </div>

        <div className="pp-section__body" style={{ padding: '1rem 1.25rem', maxHeight: '500px', overflowY: 'auto' }}>
          {/* Groups */}
          {subTab === 'groups' && (
            <CheckboxTable
              columns={[
                { key: 'GroupName', label: 'Group Name' },
                { key: 'CapCode', label: 'Cap Code' },
              ]}
              data={sortedGroups}
              idKey="GroupId"
              selected={selectedGroups}
              onToggle={(id) => toggle(setSelectedGroups, id)}
              onToggleAll={() => toggleAll(setSelectedGroups, activeGroups, 'GroupId')}
            />
          )}

          {/* Users */}
          {subTab === 'users' && (
            <CheckboxTable
              columns={[
                { key: 'UserName', label: 'Username' },
                { key: 'DisplayName', label: 'Display Name' },
                { key: 'UserRole', label: 'Role' },
              ]}
              data={sortedUsers}
              idKey="UserId"
              selected={selectedUsers}
              onToggle={(id) => toggle(setSelectedUsers, id)}
              onToggleAll={() => toggleAll(setSelectedUsers, activeUsers, 'UserId')}
            />
          )}

          {/* AFR Devices */}
          {subTab === 'afr' && (
            <CheckboxTable
              columns={[
                { key: 'device_type_name', label: 'Device Type' },
              ]}
              data={sortedAFR}
              idKey="device_type_id"
              selected={selectedAFR}
              onToggle={(id) => toggle(setSelectedAFR, id)}
              onToggleAll={() => toggleAll(setSelectedAFR, afrDataSet, 'device_type_id')}
            />
          )}

          {/* High Priority Alert Types */}
          {subTab === 'hp' && (
            <CheckboxTable
              columns={[
                { key: 'event_type_name', label: 'Event Type' },
              ]}
              data={sortedHP}
              idKey="event_type_id"
              selected={selectedHP}
              onToggle={(id) => toggle(setSelectedHP, id)}
              onToggleAll={() => toggleAll(setSelectedHP, hpDataSet, 'event_type_id')}
            />
          )}

          {/* Reason Codes */}
          {subTab === 'reasons' && (
            <CheckboxTable
              columns={[
                { key: 'reason_code_description', label: 'Description' },
              ]}
              data={sortedReasons}
              idKey="reason_code_id"
              selected={selectedReasons}
              onToggle={(id) => toggle(setSelectedReasons, id)}
              onToggleAll={() => toggleAll(setSelectedReasons, reasonDataSet, 'reason_code_id')}
            />
          )}

          {/* Alert & Custom Settings */}
          {subTab === 'alerts' && (
            <div style={{ fontSize: 'var(--font-size-sm, 0.875rem)', lineHeight: 1.8 }}>
              {ddbAlerts.map((a, i) => (
                <ul key={i} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li>Accept: <strong>{String(a.AcceptButtonEnabled).toUpperCase()}</strong></li>
                  <li>Finish: <strong>{String(a.FinishButtonEnabled).toUpperCase()}</strong></li>
                  <li>Reason: <strong>{String(a.ReasonCodeEnabled).toUpperCase()}</strong></li>
                  <li>Release: After <strong>{a.ReleaseTimeInterval}</strong> Minutes</li>
                  <li>Clear: After <strong>{a.ClearTimeInterval}</strong> Minutes</li>
                  <li>Finish: After <strong>{a.FinishTimeInterval}</strong> Minutes</li>
                  <li>Group Edit: <strong>{String(a.CaregiverChgGroupEnabled).toUpperCase()}</strong></li>
                  <li>Display Edit: <strong>{String(a.UserEditDisplayName).toUpperCase()}</strong></li>
                </ul>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MigratingTab;
